import { useMemo, useRef } from 'react';
import {
  useConfiguratorStore,
  ARTWORK_POSITIONS,
  SIZE_PRESETS,
  TEXT_LABELS,
  type PanelTab,
} from '@/state/configuratorStore';
import {
  applySvgColorOverrides,
  encodeSvgDataUrl,
  resolveTextStyle,
  decodeSvgDataUrl,
  extractSvgColors,
  isSvgDataUrl,
  normalizeHex,
  type ArtworkPositionId,
  type PatternLayer,
  type PatternRegionId,
  type TextStyleKey,
} from '@/lib/designState';

const PRESET_PALETTE = [
  '#ffffff',
  '#0f172a',
  '#245bff',
  '#ef4444',
  '#f59e0b',
  '#22c55e',
  '#a855f7',
  '#14b8a6',
  '#fb7185',
  '#64748b',
];

const TABS: { id: PanelTab; label: string }[] = [
  { id: 'colors', label: 'Colours' },
  { id: 'text', label: 'Text' },
  { id: 'logo', label: 'Artwork' },
  { id: 'patterns', label: 'Customise' },
  { id: 'size', label: 'Adjust' },
];

const TEXT_KEYS: TextStyleKey[] = ['team', 'name', 'number'];

const PATTERN_ZONE_LABELS: Record<string, string> = {
  Base: 'Body',
  Arms: 'Sleeves',
  Element1: 'Accent 1',
  Element2: 'Accent 2',
  Element3: 'Accent 3',
  Element4: 'Accent 4',
  collar: 'Collar',
  Collar: 'Collar',
  Cuff: 'Cuff',
  sleeves: 'Sleeves',
  Sleeve: 'Sleeves',
  Heel_Piece: 'Heel',
  Hem: 'Hem',
  Placket: 'Placket',
};

type PatternSlot = {
  region: PatternRegionId;
  label: string;
  hint: string;
  zoneIds?: string[];
};

const PATTERN_CORE_SLOTS: PatternSlot[] = [
  { region: 'front', label: 'Front', hint: 'Chest / torso' },
  { region: 'back', label: 'Back', hint: 'Upper back' },
  { region: 'sleeve-left', label: 'L sleeve', hint: 'Left arm' },
  { region: 'sleeve-right', label: 'R sleeve', hint: 'Right arm' },
  { region: 'collar', label: 'Collar', hint: 'Neck band', zoneIds: ['collar', 'Collar'] },
  { region: 'cuff', label: 'Cuffs', hint: 'Sleeve cuffs', zoneIds: ['Cuff', 'cuff'] },
  { region: 'hem', label: 'Hem', hint: 'Bottom hem', zoneIds: ['Hem', 'hem'] },
  { region: 'placket', label: 'Placket', hint: 'Zip / placket', zoneIds: ['Placket', 'placket'] },
];

function zoneLabel(id: string, title?: string): string {
  if (title) return title;
  if (id.toLowerCase().includes('sleeve')) return 'Sleeves';
  if (id.toLowerCase().includes('base')) return 'Body';
  if (id.toLowerCase().includes('collar')) return 'Collar';
  return PATTERN_ZONE_LABELS[id] || id;
}

function artworkPlaceLabel(a: {
  position: ArtworkPositionId;
  side: string;
  x: number;
  y: number;
  mirrorSleeves?: boolean;
}): string {
  if (a.position === 'custom') {
    const zone =
      a.y < 1050 && a.y > 680
        ? a.x < 653
          ? 'Left sleeve'
          : 'Right sleeve'
        : a.side === 'back'
          ? 'Back'
          : 'Front';
    return `${zone} · free (${Math.round(a.x)}, ${Math.round(a.y)})`;
  }
  const label = ARTWORK_POSITIONS[a.position]?.label ?? a.position;
  return a.mirrorSleeves ? `${label} + mirror` : label;
}

function sideBadge(side: string): string {
  if (side === 'back') return 'BACK';
  if (side === 'front') return 'FRONT';
  return side.toUpperCase();
}

function PositionGrid({
  value,
  onChange,
  onPlaceClick,
  onBothSleeves,
}: {
  value: ArtworkPositionId;
  onChange: (id: ArtworkPositionId) => void;
  onPlaceClick?: () => void;
  onBothSleeves?: () => void;
}) {
  const groups: { side: string; ids: ArtworkPositionId[] }[] = [
    {
      side: 'Front',
      ids: ['front-top-left', 'front-top-center', 'front-top-right', 'front-center', 'front-bottom'],
    },
    {
      side: 'Back',
      ids: ['back-top', 'back-middle', 'back-bottom', 'back-left', 'back-right'],
    },
    {
      side: 'Sleeves',
      ids: [
        'sleeve-left',
        'sleeve-right',
        'sleeve-left-top',
        'sleeve-left-bottom',
        'sleeve-right-top',
        'sleeve-right-bottom',
      ],
    },
  ];

  return (
    <div className="pos-grid">
      {groups.map(g => (
        <div key={g.side} className="pos-grid__group">
          <div className="pos-grid__label">{g.side}</div>
          <div className="pos-grid__cells">
            {g.ids.map(id => (
              <button
                key={id}
                type="button"
                className={`pos-chip${value === id ? ' is-active' : ''}`}
                onClick={() => onChange(id)}
                title={ARTWORK_POSITIONS[id].label}
              >
                {ARTWORK_POSITIONS[id].label.replace(/^[^·]+·\s*/, '')}
              </button>
            ))}
          </div>
        </div>
      ))}
      {onBothSleeves && (
        <div className="pos-grid__group">
          <div className="pos-grid__label">Both sleeves</div>
          <div className="pos-grid__cells">
            <button type="button" className="pos-chip" onClick={onBothSleeves}>
              L + R (mirror)
            </button>
          </div>
        </div>
      )}
      {onPlaceClick && (
        <div className="pos-grid__group">
          <div className="pos-grid__label">Anywhere</div>
          <div className="pos-grid__cells">
            <button
              type="button"
              className={`pos-chip${value === 'custom' ? ' is-active' : ''}`}
              onClick={onPlaceClick}
            >
              Click on model…
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SliderRow({
  label,
  min,
  max,
  value,
  unit,
  onChange,
}: {
  label: string;
  min: number;
  max: number;
  value: number;
  unit?: string;
  onChange: (n: number) => void;
}) {
  return (
    <div className="slider-row">
      <label>
        {label}
        <input
          type="range"
          min={min}
          max={max}
          value={value}
          onChange={e => onChange(Number(e.target.value))}
        />
        <em>
          {value}
          {unit ?? ''}
        </em>
      </label>
    </div>
  );
}

function NudgeRow({ onNudge }: { onNudge: (dx: number, dy: number) => void }) {
  return (
    <div className="nudge-row">
      <button type="button" className="btn btn--ghost" onClick={() => onNudge(-20, 0)} aria-label="Move left">
        ←
      </button>
      <button type="button" className="btn btn--ghost" onClick={() => onNudge(0, -20)} aria-label="Move up">
        ↑
      </button>
      <button type="button" className="btn btn--ghost" onClick={() => onNudge(0, 20)} aria-label="Move down">
        ↓
      </button>
      <button type="button" className="btn btn--ghost" onClick={() => onNudge(20, 0)} aria-label="Move right">
        →
      </button>
    </div>
  );
}

function ColorSwatches({
  value,
  onChange,
  label,
}: {
  value: string;
  onChange: (hex: string) => void;
  label: string;
}) {
  return (
    <div className="color-row">
      <span className="color-row__label">{label}</span>
      <div className="zone-row__palette">
        {PRESET_PALETTE.map(hex => (
          <button
            key={hex}
            type="button"
            className={`swatch${value?.toLowerCase() === hex ? ' is-active' : ''}`}
            style={{ background: hex }}
            title={hex}
            aria-label={`${label} ${hex}`}
            onClick={() => onChange(hex)}
          />
        ))}
        <label className="zone-row__custom">
          <input
            type="color"
            value={value}
            onChange={e => onChange(e.target.value)}
            aria-label={`${label} custom`}
          />
        </label>
      </div>
    </div>
  );
}

function layerFor(layers: PatternLayer[], region: PatternRegionId): PatternLayer | undefined {
  return layers.find(l => l.region === region);
}

function PatternsTab() {
  const template = useConfiguratorStore(s => s.template);
  const zones = useConfiguratorStore(s => s.zones);
  const designLayers = useConfiguratorStore(s => s.designLayers);
  const activeRegion = useConfiguratorStore(s => s.activePatternRegion);
  const setActiveRegion = useConfiguratorStore(s => s.setActivePatternRegion);
  const setPatternLayer = useConfiguratorStore(s => s.setPatternLayer);
  const updatePatternLayer = useConfiguratorStore(s => s.updatePatternLayer);
  const removePatternLayer = useConfiguratorStore(s => s.removePatternLayer);
  const focusPatternRegion = useConfiguratorStore(s => s.focusPatternRegion);
  const selectZone = useConfiguratorStore(s => s.selectZone);
  const fileRef = useRef<HTMLInputElement>(null);

  const slots = useMemo(() => {
    const zoneIds = new Set(zones.map(z => z.id.toLowerCase()));
    const covered = new Set(PATTERN_CORE_SLOTS.flatMap(s => (s.zoneIds ?? []).map(id => id.toLowerCase())));
    const fromZones: PatternSlot[] = zones
      .filter(z => !covered.has(z.id.toLowerCase()))
      .filter(
        z =>
          !['base', 'arms', 'sleeves', 'element1', 'element2', 'element3', 'element4'].includes(
            z.id.toLowerCase(),
          ),
      )
      .slice(0, 6)
      .map(z => ({
        region: `zone:${z.id}` as PatternRegionId,
        label: zoneLabel(z.id, z.name),
        hint: 'Template zone path',
      }));
    const core = PATTERN_CORE_SLOTS.filter(s => {
      if (!s.zoneIds) return true;
      return s.zoneIds.some(id => zoneIds.has(id.toLowerCase()));
    });
    return [...core, ...fromZones];
  }, [zones]);

  const activeLayer = layerFor(designLayers, activeRegion);
  const activeSlot = slots.find(s => s.region === activeRegion);

  const selectRegion = (region: PatternRegionId, slot?: PatternSlot) => {
    setActiveRegion(region);
    focusPatternRegion(region);
    const bare = region.startsWith('zone:') ? region.slice(5) : region;
    if (zones.some(z => z.id === bare)) selectZone(bare);
    void slot;
  };

  const layerDefaults = (): Omit<PatternLayer, 'id' | 'region' | 'src' | 'kind' | 'name'> => ({
    opacity: activeLayer?.opacity ?? 1,
    scale: activeLayer?.scale ?? 1,
    rotation: activeLayer?.rotation ?? 0,
    offsetX: activeLayer?.offsetX ?? 0,
    offsetY: activeLayer?.offsetY ?? 0,
    visible: activeLayer?.visible ?? true,
  });

  const onUpload = (file: File | undefined) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result !== 'string') return;
      setPatternLayer(activeRegion, {
        src: reader.result,
        kind: 'image',
        name: file.name.replace(/\.[^.]+$/, ''),
        colorOverrides: {},
        ...layerDefaults(),
      });
    };
    reader.readAsDataURL(file);
    if (fileRef.current) fileRef.current.value = '';
  };

  const svgText = activeLayer && isSvgDataUrl(activeLayer.src) ? decodeSvgDataUrl(activeLayer.src) : null;
  const svgColors = useMemo(() => (svgText ? extractSvgColors(svgText) : []), [svgText]);
  const overrides = activeLayer?.colorOverrides ?? {};

  const setOverride = (original: string, next: string | null) => {
    if (!activeLayer) return;
    const map = { ...overrides };
    const key = normalizeHex(original) ?? original;
    if (!next || normalizeHex(next) === key) delete map[key];
    else map[key] = normalizeHex(next) ?? next;
    updatePatternLayer(activeLayer.id, {
      colorOverrides: Object.keys(map).length ? map : undefined,
    });
  };

  const layerPreview = (layer: PatternLayer): string => {
    if (!isSvgDataUrl(layer.src) || !layer.colorOverrides || !Object.keys(layer.colorOverrides).length) {
      return layer.src;
    }
    const text = decodeSvgDataUrl(layer.src);
    if (!text) return layer.src;
    return applySvgColorOverrides(text, layer.colorOverrides)
      ? encodeSvgDataUrl(applySvgColorOverrides(text, layer.colorOverrides))
      : layer.src;
  };

  return (
    <section className="panel__section" role="tabpanel" aria-label="Customise positions">
      <h2 className="panel__title">Custom positions</h2>
      {!template ? (
        <p className="empty-note">Pick a base design on the left first.</p>
      ) : (
        <>
          <p className="empty-note" style={{ marginBottom: 10 }}>
            Base design is on the left. Here: pick an area, upload art, adjust position live.
          </p>

          <div className="slot-grid" role="listbox" aria-label="Garment areas">
            {slots.map(slot => {
              const layer = layerFor(designLayers, slot.region);
              const active = activeRegion === slot.region;
              return (
                <button
                  key={slot.region}
                  type="button"
                  role="option"
                  aria-selected={active}
                  className={`slot-card${active ? ' is-active' : ''}${layer ? ' has-pattern' : ''}`}
                  onClick={() => selectRegion(slot.region, slot)}
                  title={slot.hint}
                >
                  <div className="slot-card__preview">
                    {layer ? (
                      layer.kind === 'svg' && !isSvgDataUrl(layer.src) ? (
                        <span className="slot-card__svg-badge">SVG</span>
                      ) : (
                        <img src={layerPreview(layer)} alt="" />
                      )
                    ) : (
                      <span className="slot-card__empty">+</span>
                    )}
                  </div>
                  <div className="slot-card__meta">
                    <strong>{slot.label}</strong>
                    <span>{layer ? layer.name : 'Empty'}</span>
                  </div>
                  {layer && (
                    <span
                      className="slot-card__clear"
                      role="button"
                      tabIndex={0}
                      onClick={e => {
                        e.stopPropagation();
                        removePatternLayer(layer.id);
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.stopPropagation();
                          removePatternLayer(layer.id);
                        }
                      }}
                      aria-label={`Clear ${slot.label}`}
                    >
                      ×
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="pattern-editor">
            <div className="pattern-editor__head">
              <h3>{activeSlot?.label ?? activeRegion}</h3>
              <span className="pattern-editor__hint">{activeSlot?.hint}</span>
            </div>

            <div className="pattern-editor__actions">
              <button
                type="button"
                className="btn btn--primary pattern-editor__upload"
                onClick={() => fileRef.current?.click()}
              >
                + Upload art for this area
              </button>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*,.svg,image/svg+xml"
              hidden
              onChange={e => onUpload(e.target.files?.[0])}
            />

            {activeLayer && svgColors.length > 0 && (
              <div className="svg-colours">
                <div className="svg-colours__head">
                  <strong>SVG colours</strong>
                  <span>{svgColors.length} found</span>
                </div>
                <ul className="svg-colours__list">
                  {svgColors.map(orig => {
                    const current = overrides[orig] ?? orig;
                    const changed = normalizeHex(current) !== normalizeHex(orig);
                    return (
                      <li key={orig} className="svg-colours__row">
                        <span className="svg-colours__orig" style={{ background: orig }} title={orig} />
                        <label className="svg-colours__picker">
                          <input
                            type="color"
                            value={(normalizeHex(current) ?? orig).slice(0, 7)}
                            onChange={e => setOverride(orig, e.target.value)}
                            aria-label={`Recolour ${orig}`}
                          />
                        </label>
                        <code className="svg-colours__hex">{normalizeHex(current) ?? current}</code>
                        {changed && (
                          <button
                            type="button"
                            className="btn btn--ghost svg-colours__reset"
                            onClick={() => setOverride(orig, null)}
                          >
                            Reset
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {activeLayer && isSvgDataUrl(activeLayer.src) && svgColors.length === 0 && (
              <p className="empty-note">SVG has no editable solid colours found.</p>
            )}

            {activeLayer && (
              <div className="pattern-editor__controls">
                <SliderRow
                  label="Opacity"
                  min={10}
                  max={100}
                  value={Math.round(activeLayer.opacity * 100)}
                  unit="%"
                  onChange={v => updatePatternLayer(activeLayer.id, { opacity: v / 100 })}
                />
                <SliderRow
                  label="Scale"
                  min={25}
                  max={200}
                  value={Math.round(activeLayer.scale * 100)}
                  unit="%"
                  onChange={v => updatePatternLayer(activeLayer.id, { scale: v / 100 })}
                />
                <SliderRow
                  label="Rotate"
                  min={-180}
                  max={180}
                  value={activeLayer.rotation}
                  unit="°"
                  onChange={rotation => updatePatternLayer(activeLayer.id, { rotation })}
                />
                <SliderRow
                  label="Offset X"
                  min={-100}
                  max={100}
                  value={Math.round(activeLayer.offsetX * 100)}
                  unit="%"
                  onChange={v => updatePatternLayer(activeLayer.id, { offsetX: v / 100 })}
                />
                <SliderRow
                  label="Offset Y"
                  min={-100}
                  max={100}
                  value={Math.round(activeLayer.offsetY * 100)}
                  unit="%"
                  onChange={v => updatePatternLayer(activeLayer.id, { offsetY: v / 100 })}
                />
                <div className="pattern-editor__actions">
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={() =>
                      updatePatternLayer(activeLayer.id, { visible: !activeLayer.visible })
                    }
                  >
                    {activeLayer.visible ? 'Hide' : 'Show'}
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={() => removePatternLayer(activeLayer.id)}
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}

            {!activeLayer && (
              <p className="empty-note">
                Empty area — upload artwork to place it here, then use the sliders.
              </p>
            )}
          </div>
        </>
      )}
    </section>
  );
}

export function RightPanel() {
  const zones = useConfiguratorStore(s => s.zones);
  const selectedZoneId = useConfiguratorStore(s => s.selectedZoneId);
  const selectZone = useConfiguratorStore(s => s.selectZone);
  const setZoneColor = useConfiguratorStore(s => s.setZoneColor);
  const personalisation = useConfiguratorStore(s => s.personalisation);
  const setTextContent = useConfiguratorStore(s => s.setTextContent);
  const setTextStyle = useConfiguratorStore(s => s.setTextStyle);
  const selectPrint = useConfiguratorStore(s => s.selectPrint);
  const focusPrint = useConfiguratorStore(s => s.focusPrint);
  const selectedPrintId = useConfiguratorStore(s => s.selectedPrintId);
  const placingId = useConfiguratorStore(s => s.placingId);
  const beginPlace = useConfiguratorStore(s => s.beginPlace);
  const cancelPlace = useConfiguratorStore(s => s.cancelPlace);
  const nudgePrint = useConfiguratorStore(s => s.nudgePrint);
  const scalePrint = useConfiguratorStore(s => s.scalePrint);
  const panelTab = useConfiguratorStore(s => s.panelTab);
  const setPanelTab = useConfiguratorStore(s => s.setPanelTab);
  const size = useConfiguratorStore(s => s.size);
  const setSize = useConfiguratorStore(s => s.setSize);
  const applySizePreset = useConfiguratorStore(s => s.applySizePreset);
  const artworks = useConfiguratorStore(s => s.artworks);
  const addArtwork = useConfiguratorStore(s => s.addArtwork);
  const updateArtwork = useConfiguratorStore(s => s.updateArtwork);
  const removeArtwork = useConfiguratorStore(s => s.removeArtwork);

  const fileRef = useRef<HTMLInputElement>(null);

  const onUpload = async (file: File | undefined) => {
    if (!file) return;
    await addArtwork(file, 'front-center');
    if (fileRef.current) fileRef.current.value = '';
  };

  // Safe: only treat as text when key is team|name|number
  const rawTextKey = selectedPrintId?.startsWith('text:')
    ? (selectedPrintId.slice(5) as TextStyleKey)
    : null;
  const selectedTextKey =
    rawTextKey && TEXT_KEYS.includes(rawTextKey) ? rawTextKey : null;
  const selectedArt =
    selectedPrintId && !selectedPrintId.startsWith('text:')
      ? artworks.find(a => a.id === selectedPrintId)
      : null;

  const armPlace = () => {
    if (selectedPrintId) beginPlace(selectedPrintId);
  };

  const contentForText = (key: TextStyleKey) =>
    key === 'team' ? personalisation.teamName : key === 'name' ? personalisation.playerName : personalisation.number;

  return (
    <aside className="panel panel--right" style={{ width: 'var(--right-w)' }}>
      <div className="panel-tabs" role="tablist" aria-label="Configuration">
        {TABS.map(t => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={panelTab === t.id}
            className={`panel-tabs__btn${panelTab === t.id ? ' is-active' : ''}`}
            onClick={() => setPanelTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {panelTab === 'colors' && (
        <section className="panel__section" role="tabpanel">
          <h2 className="panel__title">Colour zones</h2>
          {zones.length === 0 && <p className="empty-note">Select a design to load zones.</p>}
          <ul className="zone-list">
            {zones.map(z => (
              <li key={z.id}>
                <button
                  type="button"
                  className={`zone-row${selectedZoneId === z.id ? ' is-active' : ''}`}
                  onClick={() => selectZone(z.id)}
                >
                  <span className="zone-row__swatch" style={{ background: z.hex }} aria-hidden />
                  <span className="zone-row__label">{zoneLabel(z.id, z.name)}</span>
                  <code className="zone-row__hex">{z.hex}</code>
                </button>
                {selectedZoneId === z.id && (
                  <div className="zone-row__palette">
                    {PRESET_PALETTE.map(hex => (
                      <button
                        key={hex}
                        type="button"
                        className="swatch"
                        style={{ background: hex }}
                        title={hex}
                        aria-label={`Set colour ${hex}`}
                        onClick={() => setZoneColor(z.id, hex)}
                      />
                    ))}
                    <label className="zone-row__custom">
                      <input
                        type="color"
                        value={z.hex}
                        onChange={e => setZoneColor(z.id, e.target.value)}
                        aria-label="Custom colour"
                      />
                    </label>
                  </div>
                )}
              </li>
            ))}
          </ul>
          <p className="empty-note" style={{ marginTop: 12 }}>
            Click the garment or a zone row, then pick a colour.
          </p>
        </section>
      )}

      {panelTab === 'text' && (
        <section className="panel__section" role="tabpanel">
          <h2 className="panel__title">Names & numbers</h2>
          <p className="empty-note" style={{ marginBottom: 12 }}>
            Edit content here. Size, colour & position → <strong>Adjust</strong> tab (or click text on the model).
          </p>

          {TEXT_KEYS.map(key => {
            const style = resolveTextStyle(key, personalisation);
            const isActive = selectedPrintId === `text:${key}`;
            return (
              <div
                key={key}
                className={`text-card${isActive ? ' is-active' : ''}`}
                onClick={() => {
                  selectPrint(`text:${key}`);
                  focusPrint(`text:${key}`);
                }}
              >
                <div className="text-card__head">
                  <strong>{TEXT_LABELS[key]}</strong>
                  <button
                    type="button"
                    className={`eye-btn${style.visible ? '' : ' is-off'}`}
                    onClick={e => {
                      e.stopPropagation();
                      setTextStyle(key, { visible: !style.visible });
                    }}
                    aria-label={style.visible ? 'Hide' : 'Show'}
                  >
                    {style.visible ? '👁' : '—'}
                  </button>
                </div>
                <label className="field">
                  <span>Text</span>
                  <input
                    type="text"
                    value={contentForText(key)}
                    onChange={e =>
                      setTextContent(
                        key,
                        key === 'number'
                          ? e.target.value.replace(/\D/g, '').slice(0, 3)
                          : e.target.value.slice(0, 24),
                      )
                    }
                    maxLength={key === 'number' ? 3 : 24}
                    onClick={e => e.stopPropagation()}
                  />
                </label>
                <ColorSwatches
                  label="Fill"
                  value={style.fill}
                  onChange={hex => setTextStyle(key, { fill: hex })}
                />
                <ColorSwatches
                  label="Outline"
                  value={style.stroke || '#0a1a4a'}
                  onChange={hex => setTextStyle(key, { stroke: hex })}
                />
                <SliderRow
                  label="Size"
                  min={24}
                  max={400}
                  value={style.fontSize}
                  unit="px"
                  onChange={fontSize => setTextStyle(key, { fontSize })}
                />
                <SliderRow
                  label="Outline"
                  min={0}
                  max={20}
                  value={style.strokeWidth}
                  onChange={strokeWidth => setTextStyle(key, { strokeWidth })}
                />
                <div className="text-card__hint">
                  x {style.x} · y {style.y} · {style.rotation}°
                </div>
              </div>
            );
          })}
        </section>
      )}

      {panelTab === 'logo' && (
        <section className="panel__section" role="tabpanel">
          <h2 className="panel__title">Logo & artwork</h2>
          <p className="empty-note" style={{ marginBottom: 12 }}>
            Upload PNG/SVG/JPG — place multiple logos. Resize & position in <strong>Adjust</strong>.
          </p>

          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={e => void onUpload(e.target.files?.[0])}
          />
          <button
            type="button"
            className="btn btn--primary"
            style={{ width: '100%', marginBottom: 12 }}
            onClick={() => fileRef.current?.click()}
          >
            + Add artwork
          </button>

          {artworks.length === 0 && (
            <p className="empty-note">No artworks yet. Upload a logo to get started.</p>
          )}

          {artworks.length > 0 && (
            <ul className="art-list">
              {artworks.map(a => (
                <li key={a.id} className={selectedPrintId === a.id ? 'is-active' : ''}>
                  <button
                    type="button"
                    className="art-row"
                    onClick={() => {
                      selectPrint(a.id);
                      focusPrint(a.id);
                    }}
                  >
                    <img src={a.dataUrl} alt="" className="art-row__thumb" />
                    <span className="art-row__meta">
                      <strong>{a.name}</strong>
                      <small>
                        {ARTWORK_POSITIONS[a.position]?.label ?? 'Custom'} · {a.w}×{a.h} ·{' '}
                        {a.side}
                      </small>
                    </span>
                    <span
                      role="button"
                      tabIndex={0}
                      className="art-row__del"
                      onClick={e => {
                        e.stopPropagation();
                        removeArtwork(a.id);
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter') removeArtwork(a.id);
                      }}
                    >
                      ×
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {selectedArt && (
            <button
              type="button"
              className="btn place-btn"
              style={{ width: '100%', marginTop: 8 }}
              onClick={() => beginPlace(selectedArt.id)}
            >
              📍 Place anywhere on model
            </button>
          )}
        </section>
      )}

      {panelTab === 'patterns' && <PatternsTab />}

      {panelTab === 'size' && (
        <section className="panel__section" role="tabpanel">
          <h2 className="panel__title">Adjust</h2>
          <p className="empty-note" style={{ marginBottom: 10 }}>
            Select a print from the list (or click it on the model), then resize / move / recolour.
          </p>

          {placingId && (
            <div className="place-banner" role="status">
              <strong>Click the garment</strong> to drop this print
              <button type="button" className="btn btn--ghost" onClick={cancelPlace}>
                Cancel
              </button>
            </div>
          )}

          {/* —— All applied prints —— */}
          <div className="applied-block">
            <div className="applied-block__head">
              <span>Applied on mockup</span>
              <em>
                {artworks.length} logo{artworks.length === 1 ? '' : 's'} · 3 text
              </em>
            </div>

            <ul className="print-list">
              {/* Text items */}
              {TEXT_KEYS.map(key => {
                const style = resolveTextStyle(key, personalisation);
                const content =
                  key === 'team'
                    ? personalisation.teamName
                    : key === 'name'
                      ? personalisation.playerName
                      : personalisation.number;
                if (!content?.trim() || !style.visible) return null;
                const active = selectedPrintId === `text:${key}`;
                return (
                  <li key={key} className={active ? 'is-active' : ''}>
                    <button
                      type="button"
                      className="print-item"
                      onClick={() => {
                        selectPrint(`text:${key}`);
                        focusPrint(`text:${key}`);
                      }}
                    >
                      <span className="print-item__thumb print-item__thumb--text" aria-hidden>
                        {key === 'number' ? '#' : key === 'name' ? 'Aa' : 'Tm'}
                      </span>
                      <span className="print-item__meta">
                        <strong>{TEXT_LABELS[key]}</strong>
                        <small>
                          “{content.trim()}” · {style.fontSize}px
                        </small>
                      </span>
                      <span className="print-item__badges">
                        <span className="loc-badge loc-badge--back">BACK</span>
                        <span className="loc-badge loc-badge--pos">
                          {style.y < 1400 ? 'Top' : style.y < 1600 ? 'Mid' : 'Low'}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}

              {/* Artwork items */}
              {artworks.map((a, i) => {
                const active = selectedPrintId === a.id;
                return (
                  <li key={a.id} className={active ? 'is-active' : ''}>
                    <button
                      type="button"
                      className="print-item"
                      onClick={() => {
                        selectPrint(a.id);
                        focusPrint(a.id);
                      }}
                    >
                      <span className="print-item__index" aria-hidden>
                        {i + 1}
                      </span>
                      <img src={a.dataUrl} alt="" className="print-item__thumb" />
                      <span className="print-item__meta">
                        <strong>{a.name}</strong>
                        <small>
                          {artworkPlaceLabel(a)} · {a.w}×{a.h}
                        </small>
                      </span>
                      <span className="print-item__badges">
                        <span
                          className={`loc-badge loc-badge--${a.side === 'back' ? 'back' : 'front'}`}
                        >
                          {sideBadge(a.side)}
                        </span>
                        {a.mirrorSleeves && <span className="loc-badge loc-badge--mirror">L+R</span>}
                        <span className="loc-badge loc-badge--pos">
                          {a.position === 'custom'
                            ? 'Free'
                            : (ARTWORK_POSITIONS[a.position]?.label.split('·')[1]?.trim() ??
                              a.position)}
                        </span>
                      </span>
                    </button>
                    <button
                      type="button"
                      className="print-item__del"
                      title="Remove"
                      onClick={e => {
                        e.stopPropagation();
                        removeArtwork(a.id);
                      }}
                    >
                      ×
                    </button>
                  </li>
                );
              })}
            </ul>

            {artworks.length === 0 && (
              <p className="empty-note" style={{ marginTop: 6 }}>
                No logos yet — add one in the Logo tab.
              </p>
            )}
          </div>

          {/* —— Text-only quick chips (when no art selected) —— */}
          {!selectedArt && (
            <div className="print-pickers" style={{ marginTop: 10 }}>
              {TEXT_KEYS.map(key => (
                <button
                  key={key}
                  type="button"
                  className={`pos-chip${selectedPrintId === `text:${key}` ? ' is-active' : ''}`}
onClick={() => {
                    selectPrint(`text:${key}`);
                    focusPrint(`text:${key}`);
                  }}
                >
                  {TEXT_LABELS[key]}
                </button>
              ))}
            </div>
          )}

          {!selectedPrintId && (
            <p className="empty-note" style={{ marginTop: 12 }}>
              Select an item above to adjust it.
            </p>
          )}

          {selectedPrintId && (
            <button
              type="button"
              className={`btn place-btn${placingId === selectedPrintId ? ' is-armed' : ''}`}
              style={{ width: '100%', marginTop: 10 }}
              onClick={armPlace}
              disabled={!!placingId && placingId !== selectedPrintId}
            >
              {placingId === selectedPrintId
                ? 'Waiting for click on model…'
                : '📍 Click on model to place freely'}
            </button>
          )}

          {selectedTextKey && (
            <div className="art-editor">
              <h3 className="panel__title" style={{ fontSize: 13 }}>
                Edit · {TEXT_LABELS[selectedTextKey]}
                <span className="loc-badge loc-badge--back" style={{ marginLeft: 8 }}>
                  BACK
                </span>
              </h3>
              <SliderRow
                label="Size"
                min={24}
                max={400}
                value={resolveTextStyle(selectedTextKey, personalisation).fontSize}
                unit="px"
                onChange={fontSize => setTextStyle(selectedTextKey, { fontSize })}
              />
              <SliderRow
                label="Rotate"
                min={-180}
                max={180}
                value={resolveTextStyle(selectedTextKey, personalisation).rotation}
                unit="°"
                onChange={rotation => setTextStyle(selectedTextKey, { rotation })}
              />
              <ColorSwatches
                label="Fill"
                value={resolveTextStyle(selectedTextKey, personalisation).fill}
                onChange={hex => setTextStyle(selectedTextKey, { fill: hex })}
              />
              <ColorSwatches
                label="Outline"
                value={resolveTextStyle(selectedTextKey, personalisation).stroke || '#0a1a4a'}
                onChange={hex => setTextStyle(selectedTextKey, { stroke: hex })}
              />
              <div className="field">
                <span>Position (back panel)</span>
                <div className="pos-grid__cells">
                  {(
                    [
                      ['back-top', 'Top'],
                      ['back-middle', 'Middle'],
                      ['back-bottom', 'Bottom'],
                      ['back-left', 'Left'],
                      ['back-right', 'Right'],
                    ] as const
                  ).map(([pid, label]) => (
                    <button
                      key={pid}
                      type="button"
                      className="pos-chip"
                      onClick={() => {
                        const a = ARTWORK_POSITIONS[pid];
                        setTextStyle(selectedTextKey, { x: a.x, y: a.y });
                        focusPrint(`text:${selectedTextKey}`);
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <NudgeRow onNudge={(dx, dy) => nudgePrint(dx, dy)} />
              <div className="scale-row">
                <button type="button" className="btn btn--ghost" onClick={() => scalePrint(0.9)}>
                  − Scale
                </button>
                <button type="button" className="btn btn--ghost" onClick={() => scalePrint(1.1)}>
                  + Scale
                </button>
              </div>
              <p className="empty-note">
                Text sits on the back by default — use Back view or “Click on model”.
              </p>
            </div>
          )}

          {selectedArt && (
            <div className="art-editor">
              <h3 className="panel__title" style={{ fontSize: 13 }}>
                Edit · {selectedArt.name}
                <span
                  className={`loc-badge loc-badge--${selectedArt.side === 'back' ? 'back' : 'front'}`}
                  style={{ marginLeft: 8 }}
                >
                  {sideBadge(selectedArt.side)}
                </span>
                {selectedArt.mirrorSleeves && (
                  <span className="loc-badge loc-badge--mirror" style={{ marginLeft: 4 }}>
                    L+R
                  </span>
                )}
              </h3>
              <p className="print-where">
                Currently on: <strong>{artworkPlaceLabel(selectedArt)}</strong>
              </p>

<div className="field">
                <span>Placement</span>
                <PositionGrid
                  value={selectedArt.position}
                  onChange={position => {
                    if (position === 'custom') {
                      beginPlace(selectedArt.id);
                      return;
                    }
                    updateArtwork(selectedArt.id, { position });
                  }}
                  onPlaceClick={() => beginPlace(selectedArt.id)}
                  onBothSleeves={() =>
                    updateArtwork(selectedArt.id, {
                      position: 'sleeve-left',
                      mirrorSleeves: true,
                    })
                  }
                />
              </div>

              <label className="check-row">
                <input
                  type="checkbox"
                  checked={selectedArt.mirrorSleeves}
                  onChange={e => {
                    const on = e.target.checked;
                    const patch: Parameters<typeof updateArtwork>[1] = { mirrorSleeves: on };
                    if (on) {
                      const onSleeve =
                        selectedArt.position.startsWith('sleeve') ||
                        (selectedArt.position === 'custom' &&
                          selectedArt.y >= 680 &&
                          selectedArt.y <= 1050);
                      if (!onSleeve) patch.position = 'sleeve-left';
                    }
                    updateArtwork(selectedArt.id, patch);
                  }}
                />
                <span>
                  Apply on <strong>both sleeves</strong>
                  {selectedArt.mirrorSleeves ? ' (mirrored L ↔ R)' : ''}
                </span>
              </label>

              <div className="pos-grid__cells" style={{ marginTop: 6 }}>
                <button
                  type="button"
                  className="pos-chip"
                  onClick={() =>
                    updateArtwork(selectedArt.id, {
                      position: 'sleeve-left-top',
                      mirrorSleeves: true,
                    })
                  }
                >
                  Sleeve tops L+R
                </button>
                <button
                  type="button"
                  className="pos-chip"
                  onClick={() =>
                    updateArtwork(selectedArt.id, {
                      position: 'sleeve-left',
                      mirrorSleeves: true,
                    })
                  }
                >
                  Sleeve middle L+R
                </button>
                <button
                  type="button"
                  className="pos-chip"
                  onClick={() =>
                    updateArtwork(selectedArt.id, {
                      position: 'sleeve-left-bottom',
                      mirrorSleeves: true,
                    })
                  }
                >
                  Cuffs L+R
                </button>
              </div>

              <SliderRow
                label="Width"
                min={40}
                max={800}
                value={selectedArt.w}
                unit="px"
                onChange={w => {
                  const ratio = selectedArt.h / Math.max(selectedArt.w, 1);
                  updateArtwork(selectedArt.id, { w, h: Math.round(w * ratio) });
                }}
              />
              <SliderRow
                label="Height"
                min={40}
                max={800}
                value={selectedArt.h}
                unit="px"
                onChange={h => updateArtwork(selectedArt.id, { h })}
              />
              <SliderRow
                label="Rotate"
                min={-180}
                max={180}
                value={selectedArt.rotation}
                unit="°"
                onChange={rotation => updateArtwork(selectedArt.id, { rotation })}
              />
              <SliderRow
                label="Opacity"
                min={10}
                max={100}
                value={Math.round((selectedArt.opacity ?? 1) * 100)}
                unit="%"
                onChange={v => updateArtwork(selectedArt.id, { opacity: v / 100 })}
              />

              <NudgeRow onNudge={(dx, dy) => nudgePrint(dx, dy)} />
              <div className="scale-row">
                <button type="button" className="btn btn--ghost" onClick={() => scalePrint(0.9)}>
                  − Scale
                </button>
                <button type="button" className="btn btn--ghost" onClick={() => scalePrint(1.1)}>
                  + Scale
                </button>
              </div>
            </div>
          )}

          <div className="garment-size">
            <h3 className="panel__title" style={{ fontSize: 13, marginTop: 8 }}>
              Garment size (order)
            </h3>
            <div className="size-chips">
              {SIZE_PRESETS.map(s => (
                <button
                  key={s.label}
                  type="button"
                  className={`size-chip${size.label === s.label ? ' is-active' : ''}`}
                  onClick={() => applySizePreset(s.label)}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <div className="size-summary">
              <strong>{size.label}</strong>
              <span>
                {size.chestCm} cm chest · {size.lengthCm} cm length · {size.sleeveCm} cm sleeve
              </span>
            </div>
            <label className="field" style={{ marginTop: 8 }}>
              <span>Custom label</span>
              <input
                type="text"
                value={size.label}
                maxLength={8}
                onChange={e => setSize({ label: e.target.value })}
              />
            </label>
          </div>
        </section>
      )}
    </aside>
  );
}
